import React, { useMemo } from "react";
import {
  Collapse,
  Icon,
  IconButton,
  ListItemText,
  Box,
} from "@mui/material";
import clsx from "clsx";
import VerticalItem from "../VerticalItem";
import IntlMessages from "../../../../../utility/IntlMessages";
import { checkPermission } from "../../../../../utility/helper/RouteHelper";
import { useAuthUser } from "../../../../../utility/AuthHooks";
import { useThemeContext } from "../../../../../utility/AppContextProvider/ThemeContextProvider";
import { useSidebarContext } from "../../../../../utility/AppContextProvider/SidebarContextProvider";
import VerticalCollapseItem from "./VerticalCollapseItem";
import { RouterConfigData } from "../../../../../../pages/routesConfig";

interface VerticalCollapseProps {
  item: RouterConfigData;
  level: number;
  activeCollapseIds: { [level: number]: string | null };
  handleToggleCollapse: (level: number, id: string) => void;
}

const VerticalCollapse: React.FC<VerticalCollapseProps> = ({
  item,
  level,
  activeCollapseIds,
  handleToggleCollapse,
}) => {
  const { theme } = useThemeContext();
  const { sidebarTextColor } = useSidebarContext();
  const { user } = useAuthUser();

  const hasPermission = useMemo(
    () => checkPermission(item.permittedRole, user.role),
    [item.permittedRole, user.role]
  );
  if (!hasPermission) return null;

  const isOpen = activeCollapseIds[level] === item.id;

  const handleClick = () => {
    handleToggleCollapse(level, item.id);
  };

  return (
    <>
      <VerticalCollapseItem
        level={level}
        sidebarTextColor={sidebarTextColor}
        button
        component="div"
        className={clsx("menu-vertical-collapse", isOpen && "open")}
        onClick={handleClick}
      >
        {item.icon && (
          <Box component="span">
            <Icon
              sx={{ mr: 4 }}
              color="action"
              className={clsx("nav-item-icon")}
            >
              {item.icon}
            </Icon>
          </Box>
        )}
        <ListItemText
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: 14,
          }}
          className="nav-item-content"
          classes={{ primary: clsx("nav-item-text") }}
          primary={<IntlMessages id={item.messageId} />}
        />
        <IconButton
          className="nav-item-icon-arrow-btn"
          sx={{ p: 0, mr: 0.75 }}
          disableRipple
          size="large"
        >
          <Icon className="nav-item-icon-arrow" color="inherit">
            {isOpen
              ? "expand_more"
              : theme.direction === "ltr"
              ? "chevron_right"
              : "chevron_left"}
          </Icon>
        </IconButton>
      </VerticalCollapseItem>

      {item.children && (
        <Collapse in={isOpen} className="collapse-children">
          {item.children.map((child) => (
            <React.Fragment key={child.id}>
              {child.type === "collapse" && (
                <VerticalCollapse
                  item={child}
                  level={level + 1}
                  activeCollapseIds={activeCollapseIds}
                  handleToggleCollapse={handleToggleCollapse}
                />
              )}
              {child.type === "item" && (
                <VerticalItem item={child} level={level + 1} />
              )}
            </React.Fragment>
          ))}
        </Collapse>
      )}
    </>
  );
};

export default React.memo(VerticalCollapse);
